import React, { useState, useEffect } from "react";
import { GarmentMeasurementResult } from "../../../utilities/types/free-trial/garment-measurement-service/types";
import MainButton from "../../button/MainButton";
import { DocumentDownload, ExportCircle } from "iconsax-react";
import * as XLSX from "xlsx";
import SelectField from "../../fields/SelectField";
import { useForm } from "react-hook-form";
import { useGarmentMeasurementServiceContext } from "../../../contexts/GarmentMeasurementServiceContext";

interface ExportResultsProps {
  measurements: GarmentMeasurementResult;
  fileName?: string;
  allMeasurements?: (GarmentMeasurementResult | null)[];
}

type ExportFormat = "json" | "text" | "csv" | "xlsx";

const formatMeasurementName = (key: string): string => {
  if (!key) return "";

  const formatted = key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim();

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const ExportResults: React.FC<ExportResultsProps> = ({ measurements, fileName = "garment-measurements", allMeasurements = [] }) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("json");
  const { measurementUnit, convertedMeasurements, uploadedImages } = useGarmentMeasurementServiceContext();

  const { control, watch } = useForm({
    defaultValues: {
      exportFormat: { value: "json", label: "JSON" },
    },
  });

  const exportFormatValue = watch("exportFormat");

  useEffect(() => {
    if (exportFormatValue && exportFormatValue.value) {
      setSelectedFormat(exportFormatValue.value as ExportFormat);
    }
  }, [exportFormatValue]);

  const formatOptions = [
    { value: "json", label: "JSON" },
    { value: "text", label: "TEXT" },
    { value: "csv", label: "CSV" },
    { value: "xlsx", label: "XLSX" },
  ];

  const convertMeasurements = (measurement: GarmentMeasurementResult) => {
    if (!measurement) return null;

    if (measurementUnit === "cm") {
      return measurement;
    }

    const CM_TO_INCH = 0.393701;
    const converted: GarmentMeasurementResult = {
      Details: { ...measurement.Details },
      MetaData: measurement.MetaData,
    };

    for (const key in measurement) {
      if (key !== "Details" && key !== "MetaData" && typeof measurement[key] === "number") {
        converted[key] = +(measurement[key] * CM_TO_INCH).toFixed(2);
      }
    }

    return converted;
  };

  const prepareMeasurementsForExport = (measurement: GarmentMeasurementResult) => {
    const data: Record<string, number> = {};

    for (const key in measurement) {
      if (key !== "Details" && key !== "MetaData" && typeof measurement[key] === "number" && measurement[key] !== 0) {
        data[key] = measurement[key];
      }
    }

    return data;
  };

  const generateContent = (format: ExportFormat): string => {
    const dataToExport = convertedMeasurements || measurements;
    const measurementData = prepareMeasurementsForExport(dataToExport);
    const hasMeasurements = Object.keys(measurementData).length > 0;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    switch (format) {
      case "json":
        return hasMeasurements ? JSON.stringify(dataToExport, null, 2) : JSON.stringify({ note: "No measurement data available for this item" }, null, 2);
      case "text":
        return [
          "===== GARMENT MEASUREMENT RESULTS =====",
          `Date: ${currentDate}`,
          `Time: ${currentTime}`,
          "---------------------------------------",
          hasMeasurements
            ? Object.entries(measurementData)
                .map(([key, value]) => `${formatMeasurementName(key).padEnd(18)}: ${value} ${measurementUnit}`)
                .join("\n")
            : "No measurement data available for this item",
          "---------------------------------------",
          `Measurement Unit: ${measurementUnit.toUpperCase()}`,
          "=======================================",
        ].join("\n");
      case "csv":
        const headers = ["Measurement", "Value", "Unit"];
        let rows: string[] = [];

        if (hasMeasurements) {
          rows = Object.entries(measurementData).map(([key, value]) => `"${formatMeasurementName(key)}","${value}","${measurementUnit}"`);
        } else {
          rows = [`"Note","No measurement data available for this item",""`];
        }

        return [`# Garment Measurement Results`, `# Exported Date: ${currentDate} ${currentTime}`, `# Measurement Unit: ${measurementUnit}`, "", headers.join(","), ...rows].join("\n");
      case "xlsx":
        return JSON.stringify(hasMeasurements ? measurementData : { note: "No measurement data available for this item" });
      default:
        return JSON.stringify(dataToExport);
    }
  };

  const getMimeType = (format: ExportFormat): string => {
    switch (format) {
      case "json":
        return "application/json";
      case "text":
        return "text/plain";
      case "csv":
        return "text/csv";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      default:
        return "application/json";
    }
  };

  const getFileExtension = (format: ExportFormat): string => {
    switch (format) {
      case "json":
        return "json";
      case "text":
        return "txt";
      case "csv":
        return "csv";
      case "xlsx":
        return "xlsx";
      default:
        return "json";
    }
  };

  const handleExport = () => {
    try {
      if (selectedFormat === "xlsx") {
        const dataToExport = convertedMeasurements || measurements;
        const measurementData = prepareMeasurementsForExport(dataToExport);
        const hasMeasurements = Object.keys(measurementData).length > 0;

        let tableData;
        if (hasMeasurements) {
          tableData = Object.entries(measurementData).map(([key, value]) => ({
            Measurement: formatMeasurementName(key),
            Value: value,
            Unit: measurementUnit,
          }));
        } else {
          tableData = [
            {
              Measurement: "Note",
              Value: "No measurement data available for this item",
              Unit: "",
            },
          ];
        }

        const ws = XLSX.utils.json_to_sheet(tableData);

        ws["!cols"] = [{ wch: 20 }, { wch: hasMeasurements ? 10 : 35 }, { wch: 8 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Measurements");

        const measurementInfoSheet = XLSX.utils.aoa_to_sheet([
          ["Garment Measurement Results"],
          [""],
          ["Exported Date", new Date().toLocaleString()],
          ["Measurement Unit", measurementUnit],
          ["Item", fileName.replace("garment-measurements-", "")],
          [""],
          ["Status", hasMeasurements ? "Data available" : "No measurement data available"],
        ]);

        XLSX.utils.book_append_sheet(wb, measurementInfoSheet, "Info");

        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return;
      }

      const content = generateContent(selectedFormat);
      const mimeType = getMimeType(selectedFormat);
      const fileExtension = getFileExtension(selectedFormat);

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.${fileExtension}`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error exporting file:", error);
      alert("Failed to export file. Please try again.");
    }
  };

  const handleExportAll = () => {
    try {
      const validMeasurements = allMeasurements && allMeasurements.length > 0 ? (allMeasurements.filter((m) => m !== null) as GarmentMeasurementResult[]) : [];

      const validImages = uploadedImages.filter((img) => img !== "");

      if (validMeasurements.length === 0 && validImages.length === 0) {
        alert("No valid measurements to export");
        return;
      }

      if (selectedFormat === "xlsx") {
        const wb = XLSX.utils.book_new();

        const allMeasurementsData: any[] = [];
        const allDetailData: any[] = [];

        if (validMeasurements.length > 0) {
          validMeasurements.forEach((measurement, index) => {
            if (!measurement) return;

            const convertedMeasurement = convertMeasurements(measurement);
            if (!convertedMeasurement) return;

            const measurementData = prepareMeasurementsForExport(convertedMeasurement);
            const hasMeasurements = Object.keys(measurementData).length > 0;

            const measurementRow: Record<string, any> = {
              Item: `Item ${index + 1}`,
              Unit: measurementUnit,
            };

            if (hasMeasurements) {
              Object.entries(measurementData).forEach(([key, value]) => {
                measurementRow[formatMeasurementName(key)] = value;
              });
            } else {
              measurementRow["Note"] = "No measurement data available for this item";
            }

            allMeasurementsData.push(measurementRow);

            if (hasMeasurements) {
              Object.entries(measurementData).forEach(([key, value]) => {
                allDetailData.push({
                  ItemNumber: index + 1,
                  Measurement: formatMeasurementName(key),
                  Value: value,
                  Unit: measurementUnit,
                });
              });
            } else {
              allDetailData.push({
                ItemNumber: index + 1,
                Measurement: "Note",
                Value: "No measurement data available for this item",
                Unit: "",
              });
            }

            if (hasMeasurements) {
              const itemTableData = Object.entries(measurementData).map(([key, value]) => ({
                Measurement: formatMeasurementName(key),
                Value: value,
                Unit: measurementUnit,
              }));

              if (itemTableData.length > 0) {
                const itemWs = XLSX.utils.json_to_sheet(itemTableData);
                itemWs["!cols"] = [{ wch: 20 }, { wch: 10 }, { wch: 8 }];
                XLSX.utils.book_append_sheet(wb, itemWs, `Item ${index + 1}`);
              }
            } else {
              const emptyItemData = [
                {
                  Measurement: "Note",
                  Value: "No measurement data available for this item",
                  Unit: "",
                },
              ];
              const itemWs = XLSX.utils.json_to_sheet(emptyItemData);
              itemWs["!cols"] = [{ wch: 20 }, { wch: 35 }, { wch: 8 }];
              XLSX.utils.book_append_sheet(wb, itemWs, `Item ${index + 1}`);
            }
          });
        }

        if (allMeasurementsData.length > 0) {
          const allMeasurementsWs = XLSX.utils.json_to_sheet(allMeasurementsData);

          const headers = Object.keys(allMeasurementsData[0]);
          const colWidths = headers.map((header) => ({ wch: Math.max(10, header.length * 1.2) }));
          allMeasurementsWs["!cols"] = colWidths;
          XLSX.utils.book_append_sheet(wb, allMeasurementsWs, "All Measurements");
        }

        if (allDetailData.length > 0) {
          const detailsWs = XLSX.utils.json_to_sheet(allDetailData);
          detailsWs["!cols"] = [{ wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 8 }];
          XLSX.utils.book_append_sheet(wb, detailsWs, "Details");
        }

        const allItemsInfoSheet = XLSX.utils.aoa_to_sheet([
          ["Garment Measurement Results - All Items"],
          [""],
          ["Exported Date", new Date().toLocaleString()],
          ["Measurement Unit", measurementUnit],
          ["Number of Items", validMeasurements.length.toString()],
          [""],
          ["Note", "Please check 'All Measurements' and 'Details' sheets for complete data"],
        ]);
        XLSX.utils.book_append_sheet(wb, allItemsInfoSheet, "Info");

        XLSX.writeFile(wb, `garment-measurements-all.xlsx`);

        return;
      }

      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      let allContent = "";

      if (validMeasurements.length > 0) {
        validMeasurements.forEach((measurement, index) => {
          if (!measurement) return;

          const convertedMeasurement = convertMeasurements(measurement);
          if (!convertedMeasurement) return;

          const measurementData = prepareMeasurementsForExport(convertedMeasurement);

          switch (selectedFormat) {
            case "json":
              allContent = allContent || "[\n";
              const jsonData = Object.keys(measurementData).length > 0 ? { ...measurementData, itemIndex: index + 1 } : { itemIndex: index + 1, note: "No measurement data available for this item" };
              allContent += (index > 0 ? ",\n" : "") + "  " + JSON.stringify(jsonData, null, 2).replace(/\n/g, "\n  ");
              if (index === validMeasurements.length - 1) {
                allContent += "\n]";
              }
              break;

            case "text":
              if (index > 0) {
                allContent += "\n\n";
              }

              const hasMeasurements = Object.keys(measurementData).length > 0;

              allContent += [
                `===== GARMENT MEASUREMENT ITEM ${index + 1} =====`,
                "---------------------------------------",
                hasMeasurements
                  ? Object.entries(measurementData)
                      .map(([key, value]) => `${formatMeasurementName(key).padEnd(20)}: ${value} ${measurementUnit}`)
                      .join("\n")
                  : "No measurement data available for this item",
                "---------------------------------------",
              ].join("\n");
              break;

            case "csv":
              if (index === 0) {
                allContent =
                  [`# Garment Measurement Results - All Items`, `# Exported Date: ${currentDate} ${currentTime}`, `# Measurement Unit: ${measurementUnit}`, "", "Item,Measurement,Value,Unit"].join(
                    "\n"
                  ) + "\n";
              }

              if (Object.keys(measurementData).length > 0) {
                Object.entries(measurementData).forEach(([key, value]) => {
                  allContent += `${index + 1},"${formatMeasurementName(key)}","${value}","${measurementUnit}"\n`;
                });
              } else {
                allContent += `${index + 1},"NOTE","No measurement data available for this item",""\n`;
              }
              break;
          }
        });
      }

      if (selectedFormat === "text" && allContent) {
        allContent = [
          "====== ALL GARMENT MEASUREMENTS ======",
          `Date: ${currentDate}`,
          `Time: ${currentTime}`,
          `Items Count: ${validMeasurements.length}`,
          `Measurement Unit: ${measurementUnit.toUpperCase()}`,
          "=======================================",
          "",
          allContent,
          "",
          "=======================================",
        ].join("\n");
      }

      const mimeType = getMimeType(selectedFormat);
      const fileExtension = getFileExtension(selectedFormat);

      const blob = new Blob([allContent], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `garment-measurements-all.${fileExtension}`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error exporting all items:", error);
      alert("Failed to export all items. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <SelectField label="Export Format" name="exportFormat" control={control} options={formatOptions} placeholder="Select format" />

        <div className="flex flex-col gap-2 md:flex-row">
          <MainButton type="primary" onClick={handleExport} customClass="w-full">
            <div className="flex items-center justify-center gap-2">
              <DocumentDownload size={20} />
              <span>
                Export as {selectedFormat.toUpperCase()} ({measurementUnit})
              </span>
            </div>
          </MainButton>

          <MainButton type="secondary" onClick={handleExportAll} customClass="w-full" disabled={!allMeasurements || allMeasurements.length <= 1}>
            <div className="flex items-center justify-center gap-2">
              <ExportCircle size={20} />
              <span>Export All Items</span>
            </div>
          </MainButton>
        </div>
      </div>
    </div>
  );
};

export default ExportResults;
